import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  Spinner,
  useTableFeatures,
  useTableSort,
  createTableColumn,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { Delete20Regular, Add20Filled, Edit20Regular } from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";

import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { DeleteIaAgent, GetAllIaAgent } from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from "../GenericComponent/NotificationSystem";

import "../CSS/ListCallFlowsComponent.css";

const ListIaAgentsComponent = ({ st_IaAgents, setIaAgents, ACTIVE_TENANT, SCOPES_NAMES }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;

  const ability = useAbility();
  const { showSuccess, showError } = useNotification();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [filterVisible, setFilterVisible] = useState({ NAME: false, SCOPE: false, VOICE: false, LANGUAGE: false });
  const [filters, setFilters] = useState({ NAME: "", SCOPE: "", VOICE: "", LANGUAGE: "" });

  // Build rows with permissions
  useEffect(() => {
    if (!st_IaAgents || !ability) return;

    const agentsArray = Array.isArray(st_IaAgents) ? st_IaAgents : Object.values(st_IaAgents || {});
    if (agentsArray.length === 0) {
      setRows([]);
      setFilteredRows([]);
      return;
    }

    const unique = Array.from(new Map(agentsArray.map((a) => [a.id, a])).values());

    const updated = unique.map((agent) => {
      const scope = agent.scope || "default";
      // Permissions reuse "Flow" module (stored in same container)
      return {
        ...agent,
        permissions: {
          main: {
            canCreate: ability?.can("create", `Flow.main@${scope}`),
            canRead: ability?.can("read", `Flow.main@${scope}`),
            canUpdate: ability?.can("update", `Flow.main@${scope}`),
            canDelete: ability?.can("delete", `Flow.main@${scope}`),
          },
        },
      };
    });

    setRows(updated);
    setFilteredRows(updated.filter((a) => a.permissions?.main?.canRead));
  }, [st_IaAgents, ability]);

  // Filtering
  useEffect(() => {
    const filtered = rows.filter((agent) => {
      const hasRead = agent.permissions?.main?.canRead;
      const voice = agent.agentPersonality?.voice || "";
      const name = agent.displayName || agent.agentPersonality?.name || "";
      const language = agent.behaviorRules?.languageAdaptation?.defaultLanguage || "";
      return (
        hasRead &&
        name.toLowerCase().includes(filters.NAME.toLowerCase()) &&
        (agent.scope || "").toLowerCase().includes(filters.SCOPE.toLowerCase()) &&
        voice.toLowerCase().includes(filters.VOICE.toLowerCase()) &&
        language.toLowerCase().includes(filters.LANGUAGE.toLowerCase())
      );
    });
    setFilteredRows(filtered);
  }, [filters, rows]);

  const columns = [
    createTableColumn({ columnId: "NAME" }),
    createTableColumn({ columnId: "SCOPE" }),
    createTableColumn({ columnId: "VOICE" }),
    createTableColumn({ columnId: "LANGUAGE" }),
    createTableColumn({ columnId: "ACTION" }),
  ];

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures(
    { columns, items: filteredRows },
    [useTableSort({ defaultSortState: { sortColumn: "NAME", sortDirection: "ascending" } })]
  );

  const sortedRows = useMemo(() => sort(getRows()), [sort, getRows]);

  const headerSortProps = (columnId) => ({
    onClick: (e) => toggleColumnSort(e, columnId),
    sortDirection: getSortDirection(columnId),
  });
  const handleFilterChange = (col, value) => setFilters((prev) => ({ ...prev, [col]: value }));
  const toggleFilterVisibility = (col) => setFilterVisible((prev) => ({ ...prev, [col]: !prev[col] }));

  const confirmRemove = (agent) => setSelectedAgent(agent);

  const removeAgent = async (agent) => {
    if (!agent.permissions?.main?.canDelete) return;
    try {
      setLoadingDeleteId(agent.id);
      const token = await getAccessToken(instance, activeAccount);
      await DeleteIaAgent(ACTIVE_TENANT, userId, agent.id, agent.scope, token);

      const scopeNames = Array.isArray(SCOPES_NAMES)
        ? SCOPES_NAMES
        : SCOPES_NAMES
          ? Object.keys(SCOPES_NAMES)
          : ["default"];
      const results = await Promise.all(
        scopeNames.map((scope) => GetAllIaAgent(ACTIVE_TENANT, userId, scope, token))
      );
      const all = results.flat();
      const unique = Array.from(new Map(all.map((a) => [a.id, a])).values());

      setIaAgents(unique);
      setSelectedAgent(null);
      showSuccess(t("IA_AGENT_DELETED_SUCCESS"));
    } catch (error) {
      console.error("Error deleting ia agent:", error);
      showError(t("IA_AGENT_DELETE_ERROR"), error?.message);
    } finally {
      setLoadingDeleteId(null);
    }
  };

  const addAgent = () => {
    navigate("/iaAgent/newIaAgent");
  };

  const selectAgent = (agent) => {
    navigate(`/iaAgent/${agent.id}`);
  };

  const hasAnyCreatePermission = SCOPES_NAMES?.some((scope) =>
    ability?.can("create", `Flow.main@${scope}`)
  );

  return (
    <>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <h2>{t("IA_AGENTS_LIST")}</h2>
          <Button appearance="primary" onClick={addAgent} disabled={!hasAnyCreatePermission}>
            <Add20Filled /> {t("ADD_NEW_IA_AGENT")}
          </Button>
        </div>
        <div className="callflow-list-table-wrapper">
          <Table sortable>
            <TableHeader>
              <TableRow>
                {["NAME", "SCOPE", "VOICE", "LANGUAGE"].map((col) => (
                  <TableHeaderCell key={col} {...headerSortProps(col)}>
                    {t(col)}
                    <CiFilter
                      className="filter-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFilterVisibility(col);
                      }}
                    />
                    {filterVisible[col] && (
                      <Input
                        className="callflow-list-input-filter-style"
                        placeholder={t("FILTER")}
                        value={filters[col]}
                        onChange={(e) => handleFilterChange(col, e.target.value)}
                      />
                    )}
                  </TableHeaderCell>
                ))}
                <TableHeaderCell className="callflow-list-cell-right">{t("ACTION")}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map(({ item, rowId }) => (
                <TableRow key={rowId}>
                  <TableCell>
                    <TableCellLayout>{item.displayName || item.agentPersonality?.name}</TableCellLayout>
                  </TableCell>
                  <TableCell><TableCellLayout>{item.scope}</TableCellLayout></TableCell>
                  <TableCell><TableCellLayout>{item.agentPersonality?.voice}</TableCellLayout></TableCell>
                  <TableCell>
                    <TableCellLayout>{item.behaviorRules?.languageAdaptation?.defaultLanguage}</TableCellLayout>
                  </TableCell>
                  <TableCell className="callflow-list-cell-right">
                    <Button
                      icon={<Edit20Regular />}
                      appearance="subtle"
                      disabled={!item.permissions?.main?.canUpdate}
                      onClick={() => selectAgent(item)}
                      title={t("EDIT")}
                    />
                    <Button
                      icon={loadingDeleteId === item.id ? <Spinner size="tiny" /> : <Delete20Regular />}
                      appearance="subtle"
                      disabled={loadingDeleteId === item.id || !item.permissions?.main?.canDelete}
                      onClick={() => confirmRemove(item)}
                      title={t("DELETE")}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {selectedAgent && (
          <ConfirmAlert
            open={!!selectedAgent}
            name={selectedAgent.displayName || selectedAgent.agentPersonality?.name}
            onConfirm={() => removeAgent(selectedAgent)}
            onCancel={() => setSelectedAgent(null)}
          />
        )}
      </div>
    </>
  );
};

export default ListIaAgentsComponent;
